import React from "react";
import { Badge, Box, EntityChipShell, Text, ROW_RUNGS } from "@salestrak/ui";

import { EntityTag, hasEntityPreview } from "#app/components/entity";
import { EntityPreview } from "#app/components/entity/entity-preview";
import type { EntityData } from "#app/lib/entity-visuals";
import type { Omittable } from "@salestrak/ui";

// The one row every access list is made of — the share dialog's principals
// and the members table's per-user workflows and items are the same row
// read from either side.

// Every row visual — avatar, team tile, workflow glyph — is the 20px `sm`
// rung on a 20px rail, so a team tile and a member circle read at one size.
export const ACCESS_VISUAL_PX = 20;
// The row rung: tall enough to seat the 28px level menu with air.
export const ACCESS_ROW_HEIGHT = ROW_RUNGS.listRow;
/** The air a scrolling band keeps above its first row and below its last. */
export const ACCESS_BAND_PADDING = 8;

// The shared row frame: a 20px visual rail, the label with an optional
// role badge or quiet meta after it, and the trailing controls. The badge,
// the meta and the controls are rigid; only the label gives way, so a
// narrow row reads "andrew.m… Admin", never two clipped fragments. `dimmed`
// is the switched-off state: the row stays, in muted ink.
export function AccessRowFrame({
  entity,
  label,
  role,
  meta,
  height = ACCESS_ROW_HEIGHT,
  dimmed = false,
  preview = false,
  children,
}: Omittable<{
  entity: EntityData;
  label: string;
  role?: string;
  meta?: string;
  height?: number;
  dimmed?: boolean;
  preview?: boolean;
  children?: React.ReactNode;
}>) {
  const visual = (
    <EntityTag
      entity={entity}
      display="visual"
      size="sm"
      visualBoxSize={ACCESS_VISUAL_PX}
      interactive={false}
    />
  );
  // Left-aligned: the frame also sits inside a pressable row, where a button's centred text would float the label.
  const name = (
    <Text size="sm" truncate={1} align="left" variant={dimmed ? "muted" : undefined}>
      {label}
    </Text>
  );
  const previewing = preview && hasEntityPreview(entity);
  return (
    <Box direction="row" align="center" gap="base" height={height}>
      {previewing ? null : visual}
      <Box flex={1} minWidth={0} direction="row" align="center" gap="sm">
        {previewing ? (
          <EntityChipShell
            ghost={false}
            renderPreview={({ close }) => <EntityPreview entity={entity} onNavigate={close} />}
          >
            <Box direction="row" align="center" gap="base" minWidth={0}>
              {visual}
              {name}
            </Box>
          </EntityChipShell>
        ) : (
          name
        )}
        {role ? <Badge size="sm">{role}</Badge> : null}
        {meta ? (
          <Box shrink={false}>
            <Text size="xs" variant="muted">
              {meta}
            </Text>
          </Box>
        ) : null}
      </Box>
      {children ? (
        <Box direction="row" align="center" gap="sm" shrink={false}>
          {children}
        </Box>
      ) : null}
    </Box>
  );
}
